/**
 *	Hooks that exist purely to support the development experience.
 *
 *	Everything here is guarded by `import.meta.hot`, which is `undefined` in a
 *	production build — Bun eliminates the branch, so the subscriptions and their
 *	imports never reach a shipped bundle.
 */
import { startTransition, useEffect, useReducer } from 'react';

import { useDocuments } from '@shared/docs/provider';

/**
 *	Re-renders a documentation page when its markdown changes on disk.
 *
 *	Returns a revision counter, intended for the rendering component's `key` so
 *	a hot update re-runs `use()` against the freshly invalidated cache.
 *
 *	A `.md` is only reachable through the dynamic `import()` in the generated
 *	manifest, which is its own hot-update boundary: Bun replaces the chunk and
 *	fires `bun:afterUpdate`, but never re-evaluates the modules above it. So
 *	nothing clears the transform cache and nothing tells React to re-render —
 *	the edit lands in the bundle and is never seen.
 *
 *	Subscribing to the update event covers both halves. The bump runs inside a
 *	transition so the currently rendered page stays on screen while the new
 *	source is re-fetched, instead of dropping to the Suspense fallback.
 */
export function useDocumentHotReload(): number {
	const { runtime } = useDocuments();
	const [revision, bump] = useReducer((value: number) => value + 1, 0);

	useEffect(() => {
		if (!import.meta.hot) {
			return;
		}

		const onUpdate = () => {
			runtime.invalidate();
			startTransition(bump);
		};

		import.meta.hot.on('bun:afterUpdate', onUpdate);

		// @NOTE Called directly, never through `import.meta.hot?.off`. Bun
		// rewrites these calls at bundle time and rejects an optional-chained
		// form at runtime with "cannot be used indirectly".
		return () => {
			import.meta.hot.off('bun:afterUpdate', onUpdate);
		};
	}, [runtime]);

	return revision;
}
